import PropTypes from 'prop-types';
import { useCallback, useMemo } from 'react';
import { useI18n } from '../../../../context/I18nContext';
import { useMatrixClient } from '../../../../context/MatrixContext';
import { useModal } from '../../../../context/ModalContext';
import { useAddContact } from '../../../../hooks/useAddContact';
import { useSelectableUserList } from '../../../../hooks/useSelectableUserList';
import SearchInput from '../../../SearchInput';
import ContactRemovalList from './ContactRemovalList';
import ContactList from '../../../ContactList';
import SettingsToggle from '../../../SettingsModal/SettingsToggle';
import './ContactsSelector.css';

/**
 * Validates if a string matches the Matrix user ID format
 * @param {string} str - The string to validate
 * @returns {boolean} - True if valid Matrix ID format
 */
const isValidMatrixId = (str) => {
  if (!str || typeof str !== 'string') return false;
  // Matrix IDs must start with @ and contain a :
  // Format: @localpart:homeserver.domain
  return str.startsWith('@') && str.includes(':') && str.length > 3;
};

const ContactsSelector = ({
  hasInvitePermissions = true,
  hasRemovePermissions = true,
  hasRenamePermissions = true,
  hasAvatarPermissions = true,
  originalContacts = [],
  selectedContacts = [],
  onRemoveContact = () => {},
  onToggleContact = () => {},
  maxSelections = null, // null means unlimited, number limits selections
  showRoomTypeToggle = false,
  isPrivateRoom = false,
  onRoomTypeChange = () => {},
  additionalUsers = [], // Previously selected users not in contact list (cached external users)
}) => {
  const { t } = useI18n();
  const client = useMatrixClient();
  const { showError } = useModal();
  const { addContact } = useAddContact();
  
  // Use unified selectable user list (known contacts + search results)
  const { users: baseUsers, searchTerm, setSearchTerm, isSearching } = useSelectableUserList();
  
  // Merge additional users (cached external users) with base users
  const users = useMemo(() => {
    if (!additionalUsers || additionalUsers.length === 0) {
      return baseUsers;
    }
    
    // Deduplicate: don't add if already in base users
    const baseUserIds = new Set(baseUsers.map(u => u.id));
    const uniqueAdditionalUsers = additionalUsers.filter(u => !baseUserIds.has(u.id));
    
    // When searching, filter additional users by search term too
    const filtered = uniqueAdditionalUsers.filter(user => {
      if (!searchTerm.trim()) return true;
      const lowerSearch = searchTerm.toLowerCase();
      const matchesName = user.name.toLowerCase().includes(lowerSearch);
      const matchesId = user.id.toLowerCase().includes(lowerSearch);
      return matchesName || matchesId;
    });
    
    return [...baseUsers, ...filtered];
  }, [baseUsers, additionalUsers, searchTerm]);

  const myUserId = client?.getUserId();
  const currentUser = (myUserId) ? client?.getUser(myUserId) : null;

  
  // Check if we've reached the selection limit (only for maxSelections > 1)
  const hasReachedLimit = maxSelections !== null && maxSelections > 1 && selectedContacts.length >= maxSelections;

  // Handle removal from the ContactsRemovalList
  const handleContactRemoval = (contactId) => {
    if (contactId !== currentUser?.id) {
      onRemoveContact(contactId);
    }
  };

  // BUG FIX 6: Improve logic flow for clearer permission and limit checks
  const isDisabled = (contactId) => {
    const isCurrentlySelected = selectedContacts.some(c => c.id === contactId);
    const isOriginalContact = originalContacts.some(c => c.id === contactId);

    // Logic for inviting (toggling ON)
    if (!isCurrentlySelected) {
      // If we don't have invite permissions, we can't add anyone new.
      // However, we can re-add someone who was part of the original list.
      if (!hasInvitePermissions && !isOriginalContact) {
        return true;
      }
      
      // Check if we've reached the limit when trying to add
      if (hasReachedLimit) {
        return true;
      }
    }

    // Logic for removing (toggling OFF)
    if (isCurrentlySelected) {
      // If we don't have remove permissions, we can't remove original members.
      // However, we can un-select someone we just added (who wasn't an original member).
      if (!hasRemovePermissions && isOriginalContact) {
        return true;
      }
    }

    return false;
  };

  /**
   * Handle inviting a user by Matrix ID - verifies and adds to search results
   */
  const handleInviteById = useCallback(async () => {
    if (!searchTerm || !isValidMatrixId(searchTerm)) return;
    
    try {
      // Verify the user exists without creating a room
      const result = await addContact(searchTerm, false);
      
      if (result && result.success) {
        // User verified - they should appear in the list via search
        // Optionally trigger a search to ensure they appear
        setSearchTerm(searchTerm); // Re-trigger search
      } else if (result && !result.success) {
        showError(
          t('ContactsSelector.inviteError'),
          result.error || t('ContactsSelector.inviteErrorDefault'),
        );
      }
    } catch (err) {
      showError(
        t('ContactsSelector.inviteError'),
        err.message || t('ContactsSelector.inviteErrorDefault'),
      );
    }
  }, [searchTerm, addContact, setSearchTerm, showError, t]);

  // Show invite button when search term is a valid Matrix ID not in the list
  const isInUserList = users.some(user => user.id.toLowerCase() === searchTerm.toLowerCase());
  const showInviteButton = searchTerm && isValidMatrixId(searchTerm) && !isInUserList;

  return (
    <div className="contacts-selector">
      <div className="contacts-selector-controls">
        <div className="contacts-selector-search-container">
          <SearchInput
            id="contacts-selector-search"
            value={searchTerm}
            placeholderValue={t('SearchInput.searchContactsId')}
            onChange={setSearchTerm}
            onClearClick={() => setSearchTerm('')}
          />
          {showInviteButton && (
            <div
              className="contacts-selector-invite-button"
              onClick={handleInviteById}
            >
              <span className="material-icons">email</span>
              {t('ContactsSelector.inviteById', { id: searchTerm })}
            </div>
          )}
        </div>
        {showRoomTypeToggle && (
          <div className="contacts-selector-room-type">
            <SettingsToggle
              label={t('ChatEdit.privateRoom')}
              description={t('ChatEdit.privateRoomDescription')}
              checked={isPrivateRoom}
              onChange={onRoomTypeChange}
            />
          </div>
        )}
        <span className="contacts-selector-instructions">
          {maxSelections === 1 
            ? 'Select one contact to start a conversation'
            : t('ChatEdit.addGuestsTipMessage')
          }
        </span>
        { !hasInvitePermissions && <span className="contacts-selector-permissions">{t('ChatEdit.noInvitePermissionsTipMessage')}</span> }
        { !hasRemovePermissions && <span className="contacts-selector-permissions">{t('ChatEdit.noRemovePermissionsTipMessage')}</span> }
        { !hasRenamePermissions && <span className="contacts-selector-permissions">{t('ChatEdit.noRenamePermissionsTipMessage')}</span> }
        { !hasAvatarPermissions && <span className="contacts-selector-permissions">{t('ChatEdit.noAvatarPermissionsTipMessage')}</span> }
        <ContactRemovalList
          contacts={selectedContacts}
          onContactRemove={handleContactRemoval}
          isRemovable={(contactId) => !isDisabled(contactId)}
        />
      </div>
      <div className="contacts-selector-list-container">
        <ContactList
          contacts={users}
          isSelectList={true}
          selectedIds={selectedContacts.map(c => c.id)}
          onSelectContact={onToggleContact}
          isLoading={isSearching}
          isToggleList={true}
          isToggled={(id) => selectedContacts.some(c => c.id === id)}
          disabled={isDisabled}
        />
      </div>
    </div>
  );
};

ContactsSelector.propTypes = {
  hasInvitePermissions: PropTypes.bool,
  hasRemovePermissions: PropTypes.bool,
  hasRenamePermissions: PropTypes.bool,
  hasAvatarPermissions: PropTypes.bool,
  originalContacts: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    name: PropTypes.string.isRequired,
  })),
  selectedContacts: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    name: PropTypes.string.isRequired,
  })),
  onRemoveContact: PropTypes.func,
  onToggleContact: PropTypes.func,
  maxSelections: PropTypes.number,
  showRoomTypeToggle: PropTypes.bool,
  isPrivateRoom: PropTypes.bool,
  onRoomTypeChange: PropTypes.func,
  additionalUsers: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    avatar: PropTypes.string,
  })),
};

export default ContactsSelector;
